import { useState } from 'react';
import { useDeviceBrowserContext } from '@archipro-design/aria';
import {
    FacebookColor,
    GoogleColor,
    LinkedinColor,
} from '@archipro-design/icons';
import { useTracker } from '@archipro-website/tracker';
import { Spinner } from '@rocco/components/spinner';
import { cn } from '@rocco/utils/cn';
import { useSocialLogins } from '~/modules/user/component/social-login/hook/use-social-logins';
import type { SocialLoginProvider } from '~/modules/user/component/social-login/type';
import type { AuthSource } from '~/modules/user/type';
import { trackAuthEvent } from '~/modules/user/util/track-auth-event';

interface AuthModalSocialButtonsProps {
    /** Providers to render; omit for all (new users). */
    loginOptions?: SocialLoginProvider[];
    authSource: AuthSource;
    redirect?: string;
    skipPostSignUp: boolean;
    className?: string;
}

const PROVIDER_ORDER: SocialLoginProvider[] = [
    'Google',
    'Facebook',
    'LinkedIn',
];

const providerIcon = (provider: SocialLoginProvider) => {
    switch (provider) {
        case 'Google':
            return <GoogleColor className="size-5 shrink-0" />;
        case 'Facebook':
            return <FacebookColor className="size-5 shrink-0" />;
        case 'LinkedIn':
            return <LinkedinColor className="size-5 shrink-0" />;
    }
};

/**
 * Social provider pill buttons for the revamped auth modal. Google renders as
 * the dark primary action; other providers render outlined. Navigation is a
 * full-page redirect through the PHP social auth controller.
 */
export const AuthModalSocialButtons = (props: AuthModalSocialButtonsProps) => {
    const { loginOptions, authSource, redirect, skipPostSignUp, className } =
        props;
    const tracker = useTracker();
    const { isInstagramOrFacebookApp } = useDeviceBrowserContext();
    const [pendingProvider, setPendingProvider] =
        useState<SocialLoginProvider>();

    const unorderedSocialLogins = useSocialLogins({
        loginOptions,
        authSource,
        redirect,
        skipPostSignUp,
        isInstagramOrFacebookApp,
    });
    // Design order: Google is the dark primary action and always leads.
    const socialLogins = [...unorderedSocialLogins].sort(
        (a, b) =>
            PROVIDER_ORDER.indexOf(a.TITLE) - PROVIDER_ORDER.indexOf(b.TITLE),
    );

    if (socialLogins.length === 0) {
        return null;
    }

    const handleClick = (provider: SocialLoginProvider) => {
        setPendingProvider(provider);
        tracker.log('accountLogin', {
            url: new URL(window.location.href),
            data: {
                step: 2,
                signInMethod: provider,
            },
        });
        trackAuthEvent({
            tracker,
            event: 'AuthSocialButtonClick',
            authSource,
            provider,
        });
    };

    return (
        <div className={cn('flex w-full flex-col gap-2.5', className)}>
            {socialLogins.map((social) => {
                const isPrimary = social.TITLE === 'Google';
                const isPending = pendingProvider === social.TITLE;
                return (
                    <a
                        key={social.KEY}
                        href={`/member/${social.URL}`}
                        data-button={social.TRACKER_ID}
                        onClick={() => handleClick(social.TITLE)}
                        className={cn(
                            'flex h-14 w-full items-center justify-center gap-2.5 rounded-full text-[1.125rem] font-medium tracking-[-0.02em] transition-colors',
                            isPrimary
                                ? 'bg-[#181818] text-[#f3f3f3] hover:bg-[#2e2e2e]'
                                : 'border border-[#9d9d9d] text-[#656565] hover:bg-black/5',
                            isPending && 'pointer-events-none opacity-70',
                        )}
                    >
                        {providerIcon(social.TITLE)}
                        <span>Continue with {social.TITLE}</span>
                        {isPending && (
                            <Spinner
                                className={cn(
                                    'size-4',
                                    isPrimary
                                        ? 'text-[#f3f3f3]'
                                        : 'text-[#656565]',
                                )}
                            />
                        )}
                    </a>
                );
            })}
        </div>
    );
};
